import React from 'react';
import { motion } from 'motion/react';
import { Table, Grid3X3, Hash, ArrowRight } from 'lucide-react';
import { MathService } from '../services/mathService';

export const SovereignTable: React.FC = () => {
  const range = Array.from({ length: 13 }, (_, i) => i);

  return (
    <div className="glass-panel p-10 border-white/5 relative overflow-hidden bg-black/60 group">
      <div className="relative z-10">
        <div className="mb-10 text-center lg:text-left">
          <h2 className="text-4xl font-display font-bold text-white mb-2 tracking-tighter uppercase italic">Sovereign <span className="text-lattice-cyan">12x Tables</span></h2>
          <p className="text-xs font-mono text-white/40 uppercase tracking-[0.3em] leading-relaxed max-w-xl">
            The Absolute Scale of Integer Presence. Expansion Logic (1x1=2) and Persistence (0xX=X).
          </p>
        </div>

        <div className="overflow-x-auto pb-6 scrollbar-hide">
          <div className="inline-grid grid-cols-[auto_repeat(13,minmax(60px,1fr))] gap-px bg-white/5 p-px rounded-xl border border-white/10 min-w-max">
            {/* Header Corner */}
            <div className="bg-black/80 p-4 flex items-center justify-center border-b border-r border-white/10">
              <Hash className="w-4 h-4 text-lattice-cyan opacity-50" />
            </div>
            
            {/* Top Header */}
            {range.map(j => (
              <div key={`header-t-${j}`} className="bg-black/80 p-4 text-center border-b border-white/10">
                <span className="text-[10px] font-mono font-bold text-lattice-cyan/60">{j}</span>
              </div>
            ))}

            {range.map(i => (
              <React.Fragment key={`row-${i}`}>
                {/* Side Header */}
                <div className="bg-black/80 p-4 text-center border-r border-white/10">
                  <span className="text-[10px] font-mono font-bold text-lattice-cyan/60">{i}</span>
                </div>
                
                {/* Data Cells */}
                {range.map(j => {
                  const res = MathService.sovereignMultiply(i, j);
                  
                  // Logic for highlighting:
                  // 1. Persistence Path (Interaction = 0)
                  // 2. Interaction Expansion (Interaction = 1)
                  // 3. Unit Expansion (Base = 1)
                  const isUnitExpansion = i === 1 && j > 1;
                  const isInteractionExpansion = j === 1;
                  const isPersistence = j === 0;

                  return (
                    <motion.div 
                      key={`${i}-${j}`}
                      whileHover={{ backgroundColor: "rgba(34, 211, 238, 0.1)" }}
                      className={`relative bg-black/40 p-4 text-center group/cell border-white/5 border-[0.5px] transition-colors duration-300 ${
                        isInteractionExpansion ? 'bg-lattice-cyan/10 ring-1 ring-inset ring-lattice-cyan/30' : 
                        isUnitExpansion ? 'bg-lattice-cyan/5 ring-1 ring-inset ring-lattice-cyan/10' :
                        isPersistence ? 'bg-white/5' : ''
                      }`}
                    >
                      <span className={`text-[10px] font-mono font-bold tracking-tighter ${
                        isInteractionExpansion || isUnitExpansion ? 'text-lattice-cyan' : 
                        isPersistence ? 'text-white/80' : 'text-white/40'
                      }`}>
                        {res}
                      </span>
                      
                      {/* Tooltip on hover */}
                      <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-white text-black text-[8px] font-mono font-bold px-2 py-1 rounded opacity-0 group-hover/cell:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                        Base:{i} Interaction:{j} = {res}
                      </div>
                    </motion.div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="mt-12 pt-10 border-t border-white/5 flex flex-wrap gap-12 justify-between items-center">
            <div className="flex items-center gap-8">
                <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-white/10 rounded-sm" />
                    <span className="text-[8px] font-mono text-white/40 uppercase tracking-[0.2em]">Persistence Path (Int:0)</span>
                </div>
                <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-lattice-cyan/40 rounded-sm" />
                    <span className="text-[8px] font-mono text-white/40 uppercase tracking-[0.2em]">Sovereign Interaction (Int:1 = +1)</span>
                </div>
                <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-black/40 border border-white/5 rounded-sm" />
                    <span className="text-[8px] font-mono text-white/40 uppercase tracking-[0.2em]">Standard Interaction (Int:n)</span>
                </div>
            </div>

            <div className="flex items-center gap-4 bg-lattice-cyan/5 px-6 py-3 rounded-full border border-lattice-cyan/10">
                <Grid3X3 className="w-4 h-4 text-lattice-cyan" />
                <span className="text-[9px] font-mono text-lattice-cyan font-bold uppercase tracking-[0.2em]">Absolute Scale Certified</span>
            </div>
        </div>
      </div>
    </div>
  );
};
